import { useState } from 'react';
import { MdAdd, MdDelete, MdEdit } from 'react-icons/md';
import InputField from 'components/fields/InputField';

interface IPackaging {
    unity: string;
    weight: number;
    ref: string;
    price: number;
    isDefault: boolean;
}

interface PackagingsManagerProps {
    packagings: IPackaging[] | undefined;
    onChange: (packagings: IPackaging[]) => void;
    baseUnity: string;
    basePrice: number;
}

const PackagingsManager = ({ packagings, onChange, baseUnity, basePrice }: PackagingsManagerProps) => {
    const [editingIndex, setEditingIndex] = useState<number | null>(null);
    const [tempWeightValue, setTempWeightValue] = useState<string>('');
    const [tempPriceValue, setTempPriceValue] = useState<string>('');

    const currentPackagings = packagings || [];

    // Normalize numeric input to accept both dots and commas
    const normalizeNumberInput = (value: string): number => {
        // Replace commas with dots for parseFloat
        const normalized = value.replace(/,/g, '.');
        const parsed = parseFloat(normalized);
        return isNaN(parsed) ? 0 : parsed;
    };

    const handleAdd = () => {
        const newPackaging: IPackaging = {
            unity: baseUnity || 'unité',
            weight: 1,
            ref: '',
            price: basePrice || 0,
            isDefault: currentPackagings.length === 0,
        };
        onChange([...currentPackagings, newPackaging]);
        const newIndex = currentPackagings.length;
        setEditingIndex(newIndex);
        setTempWeightValue('1');
        setTempPriceValue((basePrice || 0).toString());
    };

    const handleUpdate = (index: number, field: keyof IPackaging, value: any) => {
        const updated = currentPackagings.map((pkg, i) => {
            if (i === index) {
                return { ...pkg, [field]: value };
            }
            // If setting a new default, unset others
            if (field === 'isDefault' && value === true) {
                return { ...pkg, isDefault: false };
            }
            return pkg;
        });
        onChange(updated);
    };

    const handleDelete = (index: number) => {
        const filtered = currentPackagings.filter((_, i) => i !== index);
        // If we deleted the default, set first as default
        if (currentPackagings[index].isDefault && filtered.length > 0) {
            filtered[0].isDefault = true;
        }
        onChange(filtered);
        setEditingIndex(null);
        setTempWeightValue('');
        setTempPriceValue('');
    };

    const handleSetDefault = (index: number) => {
        const updated = currentPackagings.map((pkg, i) => ({
            ...pkg,
            isDefault: i === index,
        }));
        onChange(updated);
    };

    const startEditing = (index: number) => {
        setEditingIndex(index);
        setTempWeightValue(currentPackagings[index].weight.toString());
        setTempPriceValue(currentPackagings[index].price.toString());
    };

    const stopEditing = () => {
        setEditingIndex(null);
        setTempWeightValue('');
        setTempPriceValue('');
    };

    return (
        <div className="col-span-3 mt-4">
            <div className="flex items-center justify-between mb-3">
                <div>
                    <label className="text-navy-700 text-sm font-medium block">
                        Conditionnements commerciaux
                    </label>
                    <p className="text-xs text-gray-500 mt-1">
                        Définissez différents formats de vente (carton, palette, etc.)
                    </p>
                </div>
                <button
                    onClick={handleAdd}
                    className="flex items-center gap-1 px-3 py-1.5 text-xs bg-toola-500 text-white rounded-lg hover:bg-toola-600 transition-colors"
                >
                    <MdAdd size={16} />
                    Ajouter
                </button>
            </div>

            {currentPackagings.length === 0 ? (
                <div className="border border-dashed border-gray-300 rounded-lg p-4 text-center">
                    <p className="text-sm text-gray-500">
                        Aucun conditionnement défini. Le produit utilisera l'unité de base.
                    </p>
                </div>
            ) : (
                <div className="space-y-2">
                    {currentPackagings.map((pkg, index) => (
                        <div
                            key={index}
                            className={`border rounded-lg p-3 ${
                                pkg.isDefault
                                    ? 'border-toola-500 bg-toola-50/30'
                                    : 'border-gray-200 bg-white'
                            }`}
                        >
                            {editingIndex === index ? (
                                <div className="grid grid-cols-4 gap-2">
                                    <InputField
                                        label="Unité"
                                        placeholder="carton, palette..."
                                        id={`pkg-unity-${index}`}
                                        type="text"
                                        value={pkg.unity}
                                        onChange={(e: any) =>
                                            handleUpdate(index, 'unity', e.target.value)
                                        }
                                        inputSmall={true}
                                    />
                                    <InputField
                                        label={`Quantité (${baseUnity})`}
                                        placeholder="12 ou 12.5"
                                        id={`pkg-weight-${index}`}
                                        type="text"
                                        value={tempWeightValue}
                                        onChange={(e: any) => {
                                            const value = e.target.value;
                                            setTempWeightValue(value);
                                            const numValue = normalizeNumberInput(value);
                                            if (!isNaN(numValue)) {
                                                handleUpdate(index, 'weight', numValue);
                                            }
                                        }}
                                        inputSmall={true}
                                    />
                                    <InputField
                                        label="Référence"
                                        placeholder="REF-001"
                                        id={`pkg-ref-${index}`}
                                        type="text"
                                        value={pkg.ref}
                                        onChange={(e: any) =>
                                            handleUpdate(index, 'ref', e.target.value)
                                        }
                                        inputSmall={true}
                                    />
                                    <InputField
                                        label="Prix (€)"
                                        placeholder="0 ou 12.50"
                                        id={`pkg-price-${index}`}
                                        type="text"
                                        value={tempPriceValue}
                                        onChange={(e: any) => {
                                            const value = e.target.value;
                                            setTempPriceValue(value);
                                            const numValue = normalizeNumberInput(value);
                                            if (!isNaN(numValue)) {
                                                handleUpdate(index, 'price', numValue);
                                            }
                                        }}
                                        inputSmall={true}
                                    />
                                    <div className="col-span-4 flex items-center justify-between">
                                        <label className="flex items-center gap-2 text-sm cursor-pointer">
                                            <input
                                                type="checkbox"
                                                checked={pkg.isDefault}
                                                onChange={(e) =>
                                                    handleUpdate(index, 'isDefault', e.target.checked)
                                                }
                                                className="w-4 h-4 accent-toola-500"
                                            />
                                            <span className="text-gray-700">
                                                Conditionnement par défaut
                                            </span>
                                        </label>
                                        <div className="flex gap-2">
                                            <button
                                                onClick={stopEditing}
                                                className="px-3 py-1 text-xs text-gray-600 hover:text-gray-800"
                                            >
                                                Fermer
                                            </button>
                                            <button
                                                onClick={() => handleDelete(index)}
                                                className="px-3 py-1 text-xs text-red-500 hover:text-red-700 flex items-center gap-1"
                                            >
                                                <MdDelete size={14} />
                                                Supprimer
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            ) : (
                                <div className="flex items-center justify-between">
                                    <div className="flex-1">
                                        <div className="flex items-center gap-2">
                                            <span className="font-medium text-gray-900">
                                                {pkg.unity}
                                            </span>
                                            {pkg.isDefault && (
                                                <span className="px-2 py-0.5 text-xs bg-toola-500 text-white rounded">
                                                    Par défaut
                                                </span>
                                            )}
                                        </div>
                                        <div className="text-xs text-gray-600 mt-1">
                                            {pkg.ref && <span className="mr-3">Réf: {pkg.ref}</span>}
                                            <span className="mr-3">
                                                {pkg.weight} {baseUnity}
                                            </span>
                                            <span className="font-medium">{pkg.price}€</span>
                                        </div>
                                    </div>
                                    <div className="flex gap-2">
                                        {!pkg.isDefault && (
                                            <button
                                                onClick={() => handleSetDefault(index)}
                                                className="px-2 py-1 text-xs text-toola-500 hover:text-toola-700"
                                            >
                                                Définir par défaut
                                            </button>
                                        )}
                                        <button
                                            onClick={() => startEditing(index)}
                                            className="p-1.5 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded"
                                        >
                                            <MdEdit size={16} />
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default PackagingsManager;
